import React, { useEffect, useRef, useState } from 'react';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import {
  DashboardFrame,
  EmbeddingContext,
  ExperienceFrameMetadata,
  FrameOptions,
  Parameter,
  SimpleMessageEvent,
  SimpleMessageEventHandler,
  VisualFrame,
} from 'amazon-quicksight-embedding-sdk/dist/types';
import { useAppSelector } from '../../hooks/useAppSelector';
import useDashboardSelection from '../../hooks/useDashboardSelection';
import { API } from '@aws-amplify/api';
import useStudentList from '../../hooks/useStudentList';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { setSelectedStudents } from '../../redux/slices/dashboardFilterSelections';
import useAuth from '../../hooks/useAuth';
import { Role } from '../../@types/auth';
import { DashboardConfig } from '../../utils/getQuickSightDashboard';
import AnimatedLoadingLogo from '../AnimatedLoadingLogo';

interface QuickSightDashboardFilter {
  [key: string]: string[];
}

interface QuickSightDashboardEmbedProps {
  dashboardConfig: DashboardConfig;
  region?: string;
  parameters?: Parameter[];
  onMessage?: SimpleMessageEventHandler; // old handleDataChange
  tags?: string[];
  filters?: QuickSightDashboardFilter; // Keys are filter names, values are filter values
  filtersToSyncToContext?: string[]; // String names; when a filter with this name is changed, will set the same value in `filterValues`
}

interface GetQuickSightEmbedUrlProps {
  dashboardId: string;
  sheetId?: string;
  visualId?: string;
  region?: string;
  tags?: string[];
}

const PARAM_STUDENT_NAME = 'name';

const QuickSightDashboardEmbed = (props: QuickSightDashboardEmbedProps) => {
  const { filterValues, onUpdateFilterValues } = useDashboardSelection();

  if (!props.dashboardConfig) {
    console.error(`QuickSightDashboardEmbed: dashboardConfig is required`);
  }

  // State
  const dashboardRef = useRef<HTMLDivElement | null>(null);
  const [embeddingContext, setEmbeddingContext] = useState<EmbeddingContext | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [embeddedDashboard, setEmbeddedDashboard] = useState<DashboardFrame | VisualFrame | null>(
    null
  );
  const [dashboardUrl, setDashboardUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  // Hooks
  const dispatch = useAppDispatch();
  const selectedStudents = useAppSelector(
    (state) => state.dashboardFilterSelections.selectedStudents
  );

  const { user } = useAuth();
  const currentUserRole = user?.alphaAuthAttributes?.role;
  const studentList = useStudentList();

  function obtainQSFilterTag() {
    if (Role.ADMIN === currentUserRole) {
      return { key: 'student_id', value: '*' };
    } else if (Role.GUIDE === currentUserRole && user?.rawCustomData.userAttributes.level != null) {
      console.log('filtering user', { user });
      let tagValue = '*';
      if (user.rawCustomData.userAttributes.level.length > 0) {
        tagValue = user.rawCustomData.userAttributes.level;
      }
      return {
        key: 'level_id',
        value: tagValue,
      };
    } else {
      return {
        key: 'student_id',
        value: studentList?.map((student: any) => student.student_id).join('|'),
      };
    }
  }

  // Unpack non-state params
  const filtersToSyncToContext = props.filtersToSyncToContext ?? [];
  const onMessageProp = props.onMessage ?? (() => {});
  const parameters = props.parameters ?? [];

  for (const Name of filtersToSyncToContext) {
    if (Name === 'name') {
      // Get all selected student IDs, map them to names, and add those as parameters.
      // QuickSight will default to the first if multiple are provided, and should
      // handle all for multi-selects.
      const names = selectedStudents
        ?.map((student: string) => studentList?.find((a: any) => a.student_id === student)?.name)
        .filter((name: string | undefined) => !!name) as string[];

      parameters.push({
        Name: PARAM_STUDENT_NAME,
        Values: names,
      });
    } else {
      let Values: string | any[] = filterValues[Name];
      if (!Array.isArray(Values)) Values = [Values];

      // Removes all falsy things but keeps zeros
      Values = Values.filter((val: any) => (val === 0 ? true : !!val));

      parameters.push({
        Name,
        Values,
      });
    }
  }

  async function getEmbedUrl() {
    if (studentList == null || studentList.length === 0) return;
    const queryParams: GetQuickSightEmbedUrlProps = {
      dashboardId: props.dashboardConfig.dashboardId,
    };
    if (props.region != null) {
      queryParams.region = props.region;
    }
    if (props.dashboardConfig.sheetId != null && props.dashboardConfig.visualId != null) {
      queryParams.sheetId = props.dashboardConfig.sheetId;
      queryParams.visualId = props.dashboardConfig.visualId;
    }
    const tags = props.tags ?? [];
    const tag = obtainQSFilterTag();
    tags.push(`${tag.key}:${tag.value}`);
    queryParams['tags'] = tags;

    const apiName = process.env.NEXT_PUBLIC_DASH_API_NAME!;
    const path = '/get-quicksight-embed-url';
    const myInit = {
      headers: {
        'Cache-Control': 'max-age=0', // Overrides API Gateway cache
      },
      queryStringParameters: queryParams,
    };
    const json = await API.get(apiName, path, myInit);
    setDashboardUrl(json.EmbedUrl);
  }

  async function getEmbeddingContext() {
    const ec = await createEmbeddingContext();
    return setEmbeddingContext(ec);
  }

  async function onMessage(
    messageEvent: SimpleMessageEvent,
    experienceMetadata?: ExperienceFrameMetadata
  ) {
    if (messageEvent.eventName === 'PARAMETERS_CHANGED') {
      const { changedParameters } = messageEvent.message;
      const newVals: any = {};
      for (const param of changedParameters) {
        // Things to not handle
        if (filtersToSyncToContext.indexOf(param.Name) === -1) continue;

        // Special case: names need to be converted back to IDs
        if (param.Name === PARAM_STUDENT_NAME) {
          const newIDs = param.Values.map((name: string) => {
            const id = studentList?.find((a: any) => a.name === name)?.student_id;
            if (id) return id;
          });

          // Remove if role is parent or student - we don't want them deselect themselves
          dispatch(setSelectedStudents(newIDs));
        }

        // Everything else:
        else {
          let values = param.Values;

          // Reset 'ALL' to an empty array for compatibility
          if (values.length === 1 && values[0].toUpperCase && values[0].toUpperCase() === 'ALL')
            values = [];
          newVals[param.Name] = values;
        }
      }

      onUpdateFilterValues(newVals);
    }

    await onMessageProp(messageEvent, experienceMetadata);
  }

  async function embed() {
    const container = dashboardRef?.current as HTMLElement;

    // Remove all existing iframes - should only happen during React development
    // but not a bad idea to have here anyway
    for (const el of Array.from(container.querySelectorAll('iframe'))) {
      el.remove();
    }

    const options: FrameOptions = {
      url: dashboardUrl,
      container,
      height: '800px', // artbirary, for now
      resizeHeightOnSizeChangedEvent: true,
    };

    console.log('Embedding dashboard with parameters:', parameters);
    const contentOptions = {
      parameters,
      onMessage,
    };

    let newEmbeddedDashboard: DashboardFrame | VisualFrame | undefined;
    if (props.dashboardConfig.sheetId != null && props.dashboardConfig.visualId !== null) {
      newEmbeddedDashboard = await embeddingContext?.embedVisual(options, contentOptions);
    } else {
      newEmbeddedDashboard = await embeddingContext?.embedDashboard(options, contentOptions);
    }

    if (newEmbeddedDashboard != null) {
      setEmbeddedDashboard(newEmbeddedDashboard);
    } else {
      throw new Error('Failed to embed dashboard');
    }
    setIsLoading(false);
  }

  const navigateToDashboardOrSetParameters = () => {
    if ((embeddedDashboard as DashboardFrame)?.navigateToDashboard != null) {
      (embeddedDashboard as DashboardFrame)?.navigateToDashboard(
        props.dashboardConfig.dashboardId,
        {
          parameters,
        }
      );
    } else {
      embeddedDashboard?.setParameters(parameters);
    }
  };

  useEffect(() => {
    if (!dashboardUrl) getEmbedUrl();
  }, [user, studentList]);

  useEffect(() => {
    if (dashboardUrl && !embeddingContext) getEmbeddingContext();
  }, [dashboardUrl]);

  useEffect(() => {
    if (embeddingContext) embed();
  }, [embeddingContext]);

  useEffect(() => {
    navigateToDashboardOrSetParameters();
  }, [props.dashboardConfig, embeddedDashboard, parameters]);

  useEffect(() => {
    navigateToDashboardOrSetParameters();
  }, [embeddedDashboard, selectedStudents, parameters]);

  return (
    <div style={{ width: '100%', minHeight: '400px', position: 'relative' }} ref={dashboardRef}>
      {isLoading && (
        <div
          style={{
            position: 'absolute', // Position the div absolutely
            top: '50%', // Position at the middle of the parent div
            left: '50%', // Position at the middle of the parent div
            transform: 'translate(-50%, -50%)', // Offset the div by half of its width and height to center it
            zIndex: 1, // Ensure the loading logo appears above other content
          }}
        >
          <AnimatedLoadingLogo />
        </div>
      )}
    </div>
  );
};

export default QuickSightDashboardEmbed;
